import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useListGoogleFonts } from "@/react/components/SettingsApp/Site/Pages/Edit/PageSettings/CustomFonts/GoogleFonts/hooks/useListGoogleFonts";
import { FONT_WEIGHT_OPTIONS } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/Button/Editor/constants";
import { useBuilderStore } from "@/react/components/shared/BuilderUI/Hooks/useBuilderStore";
import { FormSelectNative } from "@/react/components/shared/uikit/Form/FormSelectNative";
import { FormTextSelect } from "@/react/components/shared/uikit/Form/FormTextSelect";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { FONT_FAMILY_OPTIONS } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/Text/TextStyles/constants";
import { Form } from "@circle-react-shared/uikit/Form";
import { useIsCustomFontsEnabled } from "../../../Hooks/useIsCustomFontsEnabled";
import { FormItemWrapper } from "../../DefaultEditorV2/FormItemWrapper";

export const FontWeight = ({ name }: { name: string }) => {
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();
  const { watch } = useFormContext();
  const { data: googleFonts, isLoading } = useListGoogleFonts();
  const isCustomFontsEnabled = useIsCustomFontsEnabled();

  const registerGoogleFontWeight = useBuilderStore(
    state => state.registerGoogleFontWeight,
  );

  const fontFamily = watch("styles.fontFamily") as string;

  const fontWeightOptions = useMemo(() => {
    const isWebFont = FONT_FAMILY_OPTIONS.some(
      font => font.value === fontFamily,
    );

    if (isWebFont) {
      return FONT_WEIGHT_OPTIONS;
    }

    const selectedGoogleFont = googleFonts?.fonts.find(
      font => font.family === fontFamily,
    );

    if (selectedGoogleFont) {
      return selectedGoogleFont.variants.map(variant => ({
        label: variant,
        value: variant,
      }));
    }

    return FONT_WEIGHT_OPTIONS;
  }, [googleFonts, fontFamily]);

  const handleFontWeightChange = (selectedFontWeight: string) => {
    if (fontFamily) {
      const isGoogleFont = googleFonts?.fonts.some(
        font => font.family === fontFamily,
      );

      if (isGoogleFont) {
        registerGoogleFontWeight({
          family: fontFamily,
          weight: selectedFontWeight,
        });
      }
    }
  };

  if (!isCustomFontsEnabled) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (isPropertiesPanelV2) {
    return (
      <Form.Item
        hideBorder
        isFirstChild
        hideDescription
        name={name}
        label={t("builder_ui.css_variables.typography.variables.font_weight")}
        labelClassName="!font-normal text-xs mb-0"
        childrenWrapper={FormItemWrapper}
        className="!grid grid-cols-3 !items-center !p-0"
      >
        <FormSelectNative
          name={name}
          options={fontWeightOptions}
          onChange={e => {
            const selectedFontWeight = e.currentTarget.value;
            handleFontWeightChange(selectedFontWeight);
          }}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      hideBorder
      isFirstChild
      hideDescription
      name={name}
      labelClassName="!font-normal text-xs"
      label={t("builder_ui.css_variables.typography.variables.font_weight")}
    >
      <FormTextSelect
        name={name}
        options={fontWeightOptions}
        onChange={handleFontWeightChange}
      />
    </Form.Item>
  );
};
